/**
 * 首页每周精选书籍 Tab。
 *
 * 1:1 对照 ops-weekly.jsx。书目走 A2 §2.5–§2.8 绑定 API。
 * App 独立配置栏目不存在时（复制仍是会话）不能落绑定，不假装成功。
 */

import type { HomeSectionNovelResponse } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { BookRow, HomeBlock } from '~/data/queries';
import {
  useBooks,
  useHomeBlocks,
  useHomeSectionNovels,
  useLocaleNovelCandidates,
} from '~/data/queries';
import { opsApi } from '~/hooks/use-ops';
import { BOOK_SEARCH_PLACEHOLDER } from '~/lib/book-identity';
import { langName } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DISCOVERY_REACH_LABEL, discoveryReach } from '~/lib/novel-visibility-display';
import { BookCover } from '~/ui/BookCover';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog } from '~/ui/Dialog';
import { Dialog } from '~/ui/Dialog';
import { SearchInput } from '~/ui/SearchInput';
import { Section } from '~/ui/Section';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { type OpsScopeKind, isOpsGlobal } from './ops-scope';
import { errorToast, useCanUpsertOps } from './use-ops-mutations';

/** 当前语言对应的 per-locale novel id；没有该语言行则 undefined。 */
function novelIdForLang(book: BookRow, lang: string): string | undefined {
  const id = book.languageCodes.find((l) => l.locale === lang)?.novelId;
  if (id) return id;
  if (book.displayLocale === lang && book.displayNovelId) return book.displayNovelId;
  return undefined;
}

/** 绑定行 / 候选行封面：优先该 novel 的 per-locale 图，缺则回退书组展示图。 */
function coverSrcFor(book: BookRow | undefined, novelId?: string): string | undefined {
  if (!book) return undefined;
  if (novelId) {
    const member = book.languageCodes.find((l) => l.novelId === novelId);
    if (member?.coverUrl) return member.coverUrl;
  }
  return book.coverUrl || undefined;
}

export function resolveWeeklyFeaturedSection(
  blocks: HomeBlock[],
  appId: string,
  scope: OpsScopeKind,
): HomeBlock | null {
  if (scope === 'GLOBAL' || isOpsGlobal(appId)) {
    return blocks.find((b) => b.strategyKey === 'weekly_featured' && b.appId === null) ?? null;
  }
  if (scope === 'OVERRIDE') {
    return blocks.find((b) => b.strategyKey === 'weekly_featured' && b.appId === appId) ?? null;
  }
  return null;
}

export function useWeeklyGlobalCount(lang: string): number {
  const { data: blocks } = useHomeBlocks('all', lang);
  const section = resolveWeeklyFeaturedSection(blocks, 'all', 'GLOBAL');
  const { data } = useHomeSectionNovels(section?.id, lang);
  return data.length;
}

export function WeeklyFeaturedTab({
  appId,
  lang,
  scope,
}: {
  appId: string;
  lang: string;
  scope: OpsScopeKind;
}) {
  const canWrite = useCanUpsertOps();
  const queryClient = useQueryClient();
  const { data: books } = useBooks();
  const scopeAppId = isOpsGlobal(appId) ? 'all' : appId;
  const { data: blocks } = useHomeBlocks(scopeAppId, lang);
  const section = resolveWeeklyFeaturedSection(blocks, appId, scope);
  const sectionId = section?.id;
  const limit = section?.layout === 'VERTICAL' ? 5 : 10;
  const { data: bindings, isLoading } = useHomeSectionNovels(sectionId, lang);
  const [addOpen, setAddOpen] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const { data: searchCandidates, isLoading: searchLoading } = useLocaleNovelCandidates(
    lang,
    q,
    addOpen && Boolean(q.trim()),
  );
  const matchingIds = new Set(searchCandidates.map((row) => row.novelId));

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['home-section-novels'] });

  const addMut = useMutation({
    mutationFn: (novelId: string) =>
      opsApi.createSectionNovel(sectionId as string, {
        novel_id: novelId,
        display_order: bindings.length,
      }),
    onSuccess: () => {
      void invalidate();
    },
  });
  const patchMut = useMutation({
    mutationFn: (args: { id: string; display_order: number }) =>
      opsApi.updateSectionNovel(sectionId as string, args.id, {
        display_order: args.display_order,
      }),
    onSuccess: () => {
      void invalidate();
    },
  });
  const delMut = useMutation({
    mutationFn: (id: string) => opsApi.deleteSectionNovel(sectionId as string, id),
    onSuccess: () => {
      void invalidate();
    },
  });

  const byId = useMemo(() => {
    const m = new Map<string, BookRow>();
    for (const b of books) {
      for (const loc of b.languageCodes) {
        if (loc.novelId) m.set(loc.novelId, b);
      }
      if (b.displayNovelId) m.set(b.displayNovelId, b);
    }
    return m;
  }, [books]);
  const full = bindings.length >= limit;
  const bindable = Boolean(sectionId);
  const busy = addMut.isPending || patchMut.isPending || delMut.isPending;

  const bound = new Set(bindings.map((r) => r.novel_id));
  const candidates = books
    .map((b) => {
      const novelId = novelIdForLang(b, lang);
      return novelId ? { book: b, novelId } : null;
    })
    .filter((x): x is { book: BookRow; novelId: string } => x !== null && !bound.has(x.novelId))
    .filter((x) => !q.trim() || matchingIds.has(x.novelId))
    .slice(0, 40);

  const move = async (row: HomeSectionNovelResponse, dir: -1 | 1) => {
    const i = bindings.findIndex((r) => r.id === row.id);
    const j = i + dir;
    const other = bindings[j];
    if (i < 0 || !other || !sectionId) return;
    try {
      await patchMut.mutateAsync({ id: row.id, display_order: other.display_order });
      await patchMut.mutateAsync({ id: other.id, display_order: row.display_order });
    } catch (e) {
      errorToast(e, '调整排序失败');
    }
  };

  const emptyHint =
    !bindable && scope === 'OVERRIDE'
      ? '独立配置缺少 weekly_featured 栏目行，无法绑书；请移除整份后重新复制'
      : scope === 'NO_OVERRIDE'
        ? '无覆盖 · 不继承全局书单。复制覆盖并落栏目后才能绑书'
        : scope === 'GLOBAL'
          ? '该语言全局暂无书 · 点右上角「添加书籍」（选品不能翻译，不从英语抄）'
          : '覆盖当前为空（0 本，允许为空）· 空则接口不返回';

  return (
    <>
      <Section
        pad="4px 18px 16px"
        title={`每周精选书籍 · ${lang.toUpperCase()} ${langName(lang)}`}
        sub={`手动加 · 上限 ${limit}、可以 0 · 候选只出该语言的书行 · 身份 = 书名 + 语言`}
        action={
          <div className="hstack" style={{ gap: 6 }}>
            <span className={`chip ${full ? 'chip-warn' : ''} mono`} style={{ fontSize: 10 }}>
              {bindings.length}/{limit}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-accent"
              disabled={!canWrite || !bindable || full || busy}
              title={
                !bindable ? '没有可写的每周精选栏目' : full ? `已达上限 ${limit} 本` : undefined
              }
              onClick={() => setAddOpen(true)}
            >
              <Icons.IconPlus size={11} />
              添加书籍
            </button>
          </div>
        }
      >
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 50 }}>排序</th>
              <th style={{ width: 52 }}>封面</th>
              <th>书名</th>
              <th style={{ width: 190 }}>书编码 + 语言</th>
              <th style={{ width: 190 }}>发现面触达</th>
              <th style={{ width: 92 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {bindings.map((r, i) => {
              const b = byId.get(r.novel_id);
              const member = b?.languageCodes.find((l) => l.novelId === r.novel_id);
              const title = member?.title || b?.originalTitle || b?.displayTitle || '—';
              // 「进不进发现面」按字典 §6.1「可曝光」在档位维度的投影（display 模块 discoveryReach）判，
              // 不内联档位串比较：本周精选是服务端主动选行投给用户的发现面，UNLOCKED_ONLY 与 DELISTED
              // 同样一律不进；PAYING_ONLY 只对已付费用户曝光 —— 三种情形运营的处置不同，不可压成一个布尔。
              const reach = discoveryReach(member?.visibility ?? b?.visibility ?? 'UNLOCKED_ONLY');
              return (
                <tr key={r.id}>
                  <td className="mono muted">{String(r.display_order).padStart(2, '0')}</td>
                  <td>
                    <BookCover
                      src={coverSrcFor(b, r.novel_id)}
                      hue={b?.coverHue ?? 200}
                      width={22}
                      height={30}
                      radius={3}
                    />
                  </td>
                  <td
                    style={{
                      fontSize: 12.5,
                      maxWidth: 240,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {title}
                  </td>
                  <td>
                    <div className="mono" style={{ fontSize: 11 }}>
                      {r.locale.toUpperCase()}
                    </div>
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                      {langName(r.locale)}
                    </div>
                  </td>
                  <td>
                    {reach === 'NONE' ? (
                      <span
                        className="chip chip-warn"
                        style={{ fontSize: 10 }}
                        title="该书行不进发现面 · 客户端读时过滤，绑定行不自动删"
                      >
                        {DISCOVERY_REACH_LABEL.NONE} · 接口不返回
                      </span>
                    ) : reach === 'PAYING' ? (
                      <span
                        className="chip chip-info"
                        style={{ fontSize: 10 }}
                        title="该书行只对本 App 有过成功支付的用户曝光 · 其余用户读时被过滤"
                      >
                        {DISCOVERY_REACH_LABEL.PAYING}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <div className="hstack" style={{ gap: 2 }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        style={{ width: 22, height: 22 }}
                        disabled={!canWrite || busy || i === 0}
                        title="上移"
                        onClick={() => void move(r, -1)}
                      >
                        <Icons.IconChevUp size={11} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        style={{ width: 22, height: 22 }}
                        disabled={!canWrite || busy || i === bindings.length - 1}
                        title="下移"
                        onClick={() => void move(r, 1)}
                      >
                        <Icons.IconChevDown size={11} />
                      </button>
                      <ContextMenu
                        items={[
                          {
                            label: '移除该书',
                            icon: 'IconTrash',
                            tone: 'danger',
                            disabled: !canWrite || busy,
                            onClick: () => setRemoveId(r.id),
                          },
                        ]}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          style={{ width: 22, height: 22 }}
                        >
                          <Icons.IconMoreV size={12} />
                        </button>
                      </ContextMenu>
                    </div>
                  </td>
                </tr>
              );
            })}
            {(bindings.length === 0 || isLoading) && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    padding: '26px 12px',
                    textAlign: 'center',
                    color: 'var(--fg-faint)',
                    fontSize: 11.5,
                  }}
                >
                  {isLoading ? '加载绑定…' : emptyHint}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 10, lineHeight: 1.6 }}>
          同一书组的其他语言行是另一条绑定。书库下架某书行时绑定行留着，只标「已下架 ·
          接口不返回」，不自动删。
        </div>
      </Section>

      {addOpen && sectionId && (
        <Dialog
          width={640}
          icon="IconStar"
          title="添加每周精选书籍"
          subtitle={`${isOpsGlobal(appId) ? '该语言全局' : '覆盖'} · ${lang.toUpperCase()} ${langName(lang)} · 上限 ${limit}`}
          onClose={() => setAddOpen(false)}
          footer={
            <>
              <div
                style={{
                  flex: 1,
                  fontSize: 11,
                  color: bindings.length >= limit ? 'var(--warn)' : 'var(--fg-muted)',
                }}
              >
                已绑{' '}
                <span className="mono">
                  {bindings.length}/{limit}
                </span>{' '}
                · 候选只出 <span className="mono">language={lang}</span> 的书行
              </div>
              <button type="button" className="btn" onClick={() => setAddOpen(false)}>
                关闭
              </button>
            </>
          }
        >
          <div style={{ padding: 18 }}>
            <SearchInput
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={BOOK_SEARCH_PLACEHOLDER}
              size="sm"
            />
            {searchLoading && q.trim() && <p className="muted">搜索中…</p>}
            <Table className="tbl" style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <th>书名</th>
                  <th style={{ width: 54 }} />
                </tr>
              </thead>
              <tbody>
                {candidates.map(({ book: b, novelId }) => (
                  <tr key={novelId}>
                    <td>
                      <BookCover
                        src={coverSrcFor(b, novelId)}
                        hue={b.coverHue}
                        width={20}
                        height={27}
                        radius={3}
                      />
                    </td>
                    <td
                      style={{
                        fontSize: 12,
                        maxWidth: 240,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {b.originalTitle || b.displayTitle}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={!canWrite || bindings.length >= limit || addMut.isPending}
                        onClick={() => {
                          addMut.mutate(novelId, {
                            onSuccess: () => {
                              toast(`已添加 ${b.originalTitle || b.displayTitle}`, {
                                tone: 'pos',
                                icon: 'IconPlus',
                              });
                            },
                            onError: (e) => errorToast(e, '添加失败'),
                          });
                        }}
                      >
                        添加
                      </button>
                    </td>
                  </tr>
                ))}
                {candidates.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: 20,
                        textAlign: 'center',
                        color: 'var(--fg-faint)',
                        fontSize: 11.5,
                      }}
                    >
                      该语言暂无未绑定的书
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Dialog>
      )}

      {removeId && (
        <ConfirmDialog
          title="移除该书？"
          message="仅移除该语言的这条绑定，不影响书籍本体。删单条 ≠ 移除覆盖。"
          tone="danger"
          confirmLabel="确认移除"
          onClose={() => setRemoveId(null)}
          onConfirm={() => {
            delMut.mutate(removeId, {
              onSuccess: () => {
                toast('已移除', { tone: 'warn', icon: 'IconTrash' });
                setRemoveId(null);
              },
              onError: (e) => {
                errorToast(e, '移除失败');
                setRemoveId(null);
              },
            });
          }}
        />
      )}
    </>
  );
}
